import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { AppState } from "@Store/index";
import {
  extractLanguageFromPath,
  getLanguagePreference,
  saveLanguagePreference,
  isChinaDomainClient,
} from "@Utils/languageCore";
import { updateUserLanguage } from "@App/appReducer";
import wrapperHOC from "@Utils/wrapperHOC";
import LanguagePreferenceModal from "@Components/LanguagePreferenceModal";
import { hasShownLanguagePrompt, markLanguagePromptShown } from "@Utils/languagePromptSession";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import useIsApp from "hooks/useIsApp";
import { logAppEvent } from "@Utils/appAnalytics";
import { APP_EVENTS } from "@Utils/webApputils/analyticEvents";
import { languagesForAmberUserWebsite } from "locales/langConfig/langConfig";

interface Props {
  children: React.ReactNode;
}

const LANGUAGE_SUPPORTED_ROUTES = [
  "/",
  "/places/search/",
  "/places/",
  "/forms/submit",
  "/payments/complete",
  "/payments/",
  "/profile",
  "/activity",
];

const shouldShowLanguagePrompt = (pathname: string): boolean =>
  LANGUAGE_SUPPORTED_ROUTES.some((route) => {
    if (route === "/") {
      return pathname === "/";
    }
    return pathname.startsWith(route);
  });

/**
 * Ensures language consistency across navigation
 * Shows a modal to prompt users about their language preference instead of auto-redirecting
 * This prevents SEO issues with automatic URL redirects
 *
 * NOTE: This component should only be rendered on the client side via ClientOnlyLanguageGuard
 */
const LanguageRedirectGuard: React.FC<Props> = ({ children }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const userLanguage = useSelector((state: AppState) => state.appData.userLanguage);
  const isApp = useIsApp();

  // Feature is app-only, controlled by GrowthBook
  // When enabled, shows the language preference modal in app on restarts (like website behavior)
  const isFeatureEnabled = useFeatureIsOn(
    GROWTH_BOOK_FEATURE_FLAG_NAMES.POD_APP_LANG_AND_CURRENCY_FEAT,
  );
  const shouldShowModalInApp = isApp && isFeatureEnabled;

  const [showModal, setShowModal] = useState(false);
  const [suggestedLanguage, setSuggestedLanguage] = useState<string>("");
  const hasChecked = useRef(false);

  useEffect(() => {
    const currentPath = location.pathname;
    const hasShownPromptFlag = hasShownLanguagePrompt();

    if (!hasShownPromptFlag && hasChecked.current) {
      hasChecked.current = false;
    }

    // Priority 1: Check if path has a language prefix
    const pathLanguage = extractLanguageFromPath(currentPath);
    if (pathLanguage) {
      // Path has language - sync it with Redux and save to cookie
      if (pathLanguage !== userLanguage) {
        dispatch(updateUserLanguage(pathLanguage));
      }
      saveLanguagePreference(pathLanguage);
      hasChecked.current = true;
      return;
    }

    // Show modal in app only if feature flag is enabled
    if (isApp && !shouldShowModalInApp) {
      hasChecked.current = true;
      return;
    }

    // Only check once per session for modal prompt
    if (hasChecked.current || hasShownPromptFlag) {
      return;
    }

    // Only show prompt for supported routes
    if (!shouldShowLanguagePrompt(currentPath)) {
      hasChecked.current = true;
      return;
    }

    // Priority 2: Check cookie for saved language preference
    const storedLanguage = getLanguagePreference();

    // Special case: If user has Chinese (zh) cookie but is NOT on Chinese domain,
    // replace it with English (en) as default since Chinese requires a different domain
    const isOnChinaDomain = isChinaDomainClient();
    if (storedLanguage === "zh" && !isOnChinaDomain) {
      saveLanguagePreference("en");
      if (userLanguage !== "en") {
        dispatch(updateUserLanguage("en"));
      }
      hasChecked.current = true;
      return;
    }

    // If no cookie exists, save "en" as default
    if (!storedLanguage) {
      saveLanguagePreference("en");
      if (userLanguage !== "en") {
        dispatch(updateUserLanguage("en"));
      }
      hasChecked.current = true;
      return;
    }

    if (storedLanguage !== "en" && storedLanguage !== "zh") {
      saveLanguagePreference("en"); // Default to English until user explicitly confirms
      setSuggestedLanguage(storedLanguage);
      setShowModal(true);
      markLanguagePromptShown();

      const languageConfig = languagesForAmberUserWebsite.find(
        (lang) => lang.code === storedLanguage,
      );
      const languageName = languageConfig?.nativeLabel || storedLanguage;

      logAppEvent({
        eventName: APP_EVENTS.MODAL_INTERACTED,
        payload: {
          modal_name: "language_preference_modal",
          action: "shown",
          suggested_language: storedLanguage,
          suggested_language_name: languageName,
          current_path: currentPath,
        },
      });
    } else if (storedLanguage !== userLanguage) {
      dispatch(updateUserLanguage(storedLanguage));
    }

    hasChecked.current = true;
  }, [location.pathname, userLanguage, dispatch, isApp, shouldShowModalInApp]);

  const handleCloseModal = () => {
    setShowModal(false);
  };

  return (
    <>
      <LanguagePreferenceModal
        isOpen={showModal}
        onClose={handleCloseModal}
        suggestedLanguage={suggestedLanguage}
        currentPath={location.pathname}
      />
      {children}
    </>
  );
};

export default wrapperHOC(LanguageRedirectGuard, {
  componentName: "LanguageRedirectGuard",
});
